import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import { WhitelistedEmails } from '@mint-sdk/api-data-ingestion';

import { useDeleteWhitelistedEmail } from '../../queries/index.js';

type WhitelistedEmailDeleteDialogProps = {
  email: WhitelistedEmails.WhitelistedEmail;
  onClose: () => void;
};

export const WhitelistedEmailDeleteDialog = ({
  email,
  onClose,
}: WhitelistedEmailDeleteDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const { mutateAsync: deleteEmail, isPending: isSubmitting } =
    useDeleteWhitelistedEmail();

  const handleSubmit = async () => {
    try {
      await deleteEmail(email.id);
      onClose();
    } catch {
      /* empty */
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center justify-center gap-2">
              <WarningAltFilled className="size-5 fill-error-main" />
              <p>{t('Delete trusted email?')}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent>
            <DialogDescription>
              {t(
                'This email will no longer be trusted for auto-import once it is removed from the whitelist.',
              )}
            </DialogDescription>
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              color="error"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {t('Yes, delete')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
